<template>
    <form class="form shuttle-search-form" aria-label="Shuttle booking search form" @submit.prevent="submitForm">
        <div class="grid grid-cols-2 xl:grid-cols-9 w-full">
            <div class="col-span-2 xl:col-span-4 order-1">
                <LazyUiSearchboxComponentsDestination
                    v-model:value="formData.pickUp"
                    name="pickup"
                    :title="t('forms.shuttles.pickupLocationTitle')"
                    :placeholder="t('forms.shuttles.pickupLocationPlaceholder')"
                    :google="true"
                    :only-airports="false"
                    :validation="v$.pickUp"
                />
            </div>

            <div class="col-span-2 xl:col-span-4 order-2">
                <LazyUiSearchboxComponentsDestination
                    v-model:value="formData.dropOff"
                    name="dropoff"
                    :title="t('forms.shuttles.dropOffTitle')"
                    :placeholder="t('forms.shuttles.dropOffPlaceholder')"
                    :google="true"
                    :only-airports="false"
                    :validation="v$.dropOff"
                    date-format="day, mmm dd"
                />
            </div>

            <div class="col-span-1 xl:col-span-1 order-3">
                <SimpleSelect
                    v-model:value="formData.passengers"
                    :title="t('forms.shuttles.passengerTitle')"
                    name="pass"
                    :options="createArrayOfObjects(Number(passengersMaxCount) || 50)"
                    prepend-icon="user-3-fill"
                    class="passengers-select"
                />
            </div>

            <div class="col-span-2 xl:col-span-3 order-5 xl:order-4">
                <LazyUiSearchboxComponentsDateTimePicker
                    v-model:value="formData.dateDep"
                    :label="depLabels"
                    name="departure_date"
                    :min-date="new Date()"
                    :date-picker-title="t('forms.shuttles.selectDepartureDateTitle')"
                    :validation="v$.dateDep"
                    :apr="apr"
                    date-format="day, mmm dd"
                    :time-format="hoursTimeFormat"
                    :time-increments="15"
                />
            </div>

            <div class="col-span-1 order-4 xl:order-5">
                <LazyUiSearchboxComponentsToggle
                    v-model:value="formData.returnTrip"
                    name="checkbox"
                    :label="t('forms.shuttles.bookReturnSwitcherLabel')"
                />
            </div>

            <div v-if="formData.returnTrip" class="col-span-2 xl:col-span-3 order-6">
                <LazyUiSearchboxComponentsDateTimePicker
                    v-model:value="formData.dateRep"
                    :label="repLabels"
                    name="arrival_date"
                    :min-date="formData.dateDep.date ? new Date(formData.dateDep.date) : new Date()"
                    :date-picker-title="t('forms.shuttles.selectArrivalDateTitle')"
                    :validation="v$.dateRep"
                    :apr="apr"
                    date-format="day, mmm dd"
                    :time-format="hoursTimeFormat"
                    :time-increments="15"
                />
            </div>

            <AdvancedBtn
                v-if="apr"
                class="col-span-2 xl:col-start-8 xl:col-end-10 order-7 shuttle-btn"
                component-variant="primary"
                component-type="button"
                button-type="submit"
                :with-arrow="true"
                :is-loading="loading"
            >
                {{ t('forms.shuttles.searchBtnText') }}
            </AdvancedBtn>

            <div v-else class="input-group-container submit col-span-2 xl:col-start-8 xl:col-end-10 order-7">
                <span class="title">{{ t('forms.shuttles.submitBtnText') }}</span>
                <button type="submit" :disabled="loading">
                    <template v-if="!loading">{{ t('forms.shuttles.searchBtnText') }}</template>
                    <NuxtIcon v-else name="ri:refresh-line" class="animate-spin loading-icon" />
                </button>
            </div>

            <div class="col-span-2 xl:col-start-8 xl:col-end-10 order-last custom-label" v-if="isShuttlesLogo">
                <small class="inline-flex items-center gap-2">
                    <span>{{ t('forms.shuttles.redirectModal.poweredBy') }}</span>
                    <SfLogo
                        class="h-5 w-auto align-[-2px] -mt-1"
                        :aria-label="t('forms.shuttles.redirectModal.partnerName')"
                    />
                </small>
            </div>
            <div class="col-span-2 xl:col-start-8 xl:col-end-10 order-last custom-label" v-else>
                <small>
                    {{ t('forms.shuttles.redirectModal.poweredBy') }}
                    <a href="https://www.shuttlefinder.com/" rel="nofollow">
                        {{ t('forms.shuttles.redirectModal.partnerName') }}
                    </a>
                </small>
            </div>
        </div>

        <ShuttleRedirectModal
            v-if="isShuttlesRedirectModal"
            :is-open="isRedirecting || showError"
            :is-loading="isRedirecting && !showError"
            :has-error="showError"
            :show-logo="isShuttlesLogo"
            @close="handleModalClose"
        />
    </form>
</template>

<script setup lang="ts">
import SimpleSelect from '@nuxt-monorepo/ui/components/Searchbox/Components/SimpleSelect.vue';
import AdvancedBtn from '@nuxt-monorepo/ui/components/AdvancedBtn.vue';
import ShuttleRedirectModal from '~/components/Searchbox/Components/ShuttleRedirectModal.vue';
import { helpers, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import type { ISFSearch, ISFSearchFormData } from '@nuxt-monorepo/ui/types/components/Searchbox';
import { defineAsyncComponent } from 'vue';

const SfLogo = defineAsyncComponent(() => import('~/../../apps/sf/app/components/Logo/Main.vue'));

const props = withDefaults(
    defineProps<{
        loading: boolean;
        error?: boolean;
        apr?: boolean;
        timeFormat?: string;
        passengersMaxCount?: string | number;
        isShuttlesRedirectModal?: boolean;
        isShuttlesLogo?: boolean;
    }>(),
    {
        error: false,
        passengersMaxCount: 50,
        isShuttlesRedirectModal: false,
        isShuttlesLogo: false,
    }
);

const { t } = useI18n();
const emit = defineEmits<{
    (e: 'submitSearch', query: ISFSearch, sf: boolean): void;
}>();

const isRedirecting = ref(false);
const showError = ref(false);
let redirectTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
    () => props.loading,
    (val, oldVal) => {
        if (oldVal && !val) {
            isRedirecting.value = false;
            clearRedirectTimeout();
        }
    }
);

watch(
    () => props.error,
    (val) => {
        if (val) {
            showError.value = true;
            isRedirecting.value = false;
            clearRedirectTimeout();
        }
    }
);

const clearRedirectTimeout = () => {
    if (redirectTimeout) {
        clearTimeout(redirectTimeout);
        redirectTimeout = null;
    }
};

const handleModalClose = () => {
    isRedirecting.value = false;
    showError.value = false;
    clearRedirectTimeout();
};

const formData: Ref<ISFSearchFormData> = ref({
    pickUp: null,
    dropOff: null,
    returnTrip: false,
    passengers: '1',
    dateDep: {
        date: '',
        time: '',
    },
    dateRep: {
        date: '',
        time: '',
    },
});

const createArrayOfObjects = (n: number) =>
    Array.from({ length: n }, (_, i) => ({
        label: (i + 1).toString(),
        value: (i + 1).toString(),
    }));

const checkReturnDate = () => {
    return new Date(formData.value?.dateDep?.date) < new Date(formData.value?.dateRep?.date);
};

const rules = computed(() => {
    return {
        pickUp: {
            required: helpers.withMessage(t('forms.shuttles.errors.pickUpLocationRequired'), required),
        },
        dropOff: {
            required: helpers.withMessage(t('forms.shuttles.errors.dropOffLocationRequired'), required),
        },
        dateDep: {
            date: {
                required: helpers.withMessage(t('forms.shuttles.errors.departDateRequired'), required),
            },
            time: {
                required: helpers.withMessage(t('forms.shuttles.errors.departTimeRequired'), required),
            },
        },
        dateRep: formData.value.returnTrip
            ? {
                  date: {
                      required: helpers.withMessage(t('forms.shuttles.errors.returnDateRequired'), required),
                      checkReturnDate: helpers.withMessage(
                          t('forms.shuttles.returnDateBeforeInitialDeparture'),
                          checkReturnDate
                      ),
                  },
                  time: {
                      required: helpers.withMessage(t('forms.shuttles.errors.returnTimeRequired'), required),
                  },
              }
            : {},
    };
});

const v$ = useVuelidate(rules, formData);

const tripType = computed(() => {
    let tripType = 'nonAirport';

    const locationType1 = formData.value.pickUp?.type;
    const locationType2 = formData.value.dropOff?.type;

    const isType1Airport = ['airport', 'cruiseport'].includes(locationType1 as string);
    const isType2Airport = ['airport', 'cruiseport'].includes(locationType2 as string);

    if (locationType1 && locationType2) {
        tripType =
            isType1Airport && isType2Airport
                ? 'airportToAirport'
                : isType1Airport
                  ? 'fromAirport'
                  : isType2Airport
                    ? 'toAirport'
                    : 'nonAirport';
    }

    return tripType;
});

const depLabels = computed(() => {
    let fieldLabel: string;
    switch (tripType.value) {
        case 'toAirport':
            fieldLabel = 'Flight Departure';
            break;
        case 'fromAirport':
        case 'airportToAirport':
            fieldLabel = 'Flight Arrival';
            break;
        default:
            fieldLabel = 'Pick Up';
            break;
    }

    return {
        global: `${fieldLabel} Date & Time`,
        date: `${fieldLabel} Date`,
        time: `${fieldLabel} Time`,
    };
});

const repLabels = computed(() => {
    let fieldLabel: string;
    switch (tripType.value) {
        case 'toAirport':
            fieldLabel = 'Return Flight Arrival';
            break;
        case 'fromAirport':
            fieldLabel = 'Return Flight Departure';
            break;
        case 'airportToAirport':
            fieldLabel = 'Return Flight Arrival';
            break;
        default:
            fieldLabel = 'Return Pick Up';
            break;
    }

    return {
        global: `${fieldLabel} Date & Time`,
        date: `${fieldLabel} Date`,
        time: `${fieldLabel} Time`,
    };
});

const formatDateTime = (dateTimeObj) => {
    if (!dateTimeObj.date || !dateTimeObj.time) return null;

    const date = new Date(dateTimeObj.date);
    const datePart = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    if (!props.apr) {
        // 24-hour format (default)
        // If time already includes AM/PM, convert it to 24-hour
        const timeStr = dateTimeObj.time.trim();
        if (timeStr.includes('AM') || timeStr.includes('PM')) {
            const isPM = timeStr.includes('PM');
            const [hourStr, minuteStr] = timeStr.replace(/\s*(AM|PM)/i, '').split(':');
            let hour = parseInt(hourStr, 10);
            const minute = minuteStr || '00';

            // Convert to 24-hour format
            if (isPM && hour !== 12) hour += 12;
            if (!isPM && hour === 12) hour = 0;

            return `${datePart} ${String(hour).padStart(2, '0')}:${minute}:00`;
        }
        return `${datePart} ${timeStr}:00`;
    }

    if (dateTimeObj.time.includes('AM') || dateTimeObj.time.includes('PM')) {
        // Shuttlefinder expects 24-hour format
        return `${datePart} ${dateTimeObj.time}`;
    }

    // 12-hour format with AM/PM (for APR)
    const timeStr = dateTimeObj.time.trim();

    // If time already has AM/PM, just use it
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
        const [timePart, period] = timeStr.split(/\s+/);
        const [hourStr, minuteStr] = timePart.split(':');
        const hour = parseInt(hourStr, 10);
        const minute = minuteStr || '00';
        return `${datePart} ${hour}:${minute} ${period}`;
    }

    // Convert 24-hour to 12-hour with AM/PM
    const [hourStr, minuteStr] = timeStr.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || '00';

    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // Convert 0 to 12 for 12 AM

    return `${datePart} ${hour}:${minute} ${ampm}`;
};

const submitForm = () => {
    v$.value.$validate();

    if (!v$.value.$error) {
        isRedirecting.value = true;
        showError.value = false;

        redirectTimeout = setTimeout(() => {
            if (isRedirecting.value) {
                isRedirecting.value = false;
                showError.value = true;
            }
        }, 15000);

        const query: ISFSearch = {
            trip_type: tripType.value,
            pickup: {
                place_id: formData.value.pickUp!.place_id!,
            },
            dropoff: {
                place_id: formData.value.dropOff!.place_id!,
            },
            pickup_date: tripType.value === 'nonAirport' ? formatDateTime(formData.value.dateDep) : null,
            dropoff_date: tripType.value != 'nonAirport' ? formatDateTime(formData.value.dateDep) : null,
            return_pickup_date: tripType.value === 'nonAirport' ? formatDateTime(formData.value.dateRep) : null,
            return_dropoff_date: tripType.value != 'nonAirport' ? formatDateTime(formData.value.dateRep) : null,
            passengers: Number(formData.value.passengers),
            return: formData.value.returnTrip,
            referred_by: '10',
        };

        emit('submitSearch', query, true);
    }
};

const hoursTimeFormat = computed(() => (props.timeFormat === 'GB' ? '12h' : '24h'));

onUnmounted(() => {
    clearRedirectTimeout();
});
</script>

<style scoped lang="postcss">
.shuttle-search-form {
    :deep(.dates-container .vc-pane-container .vc-pane-header-wrapper) {
        @apply top-0;
    }
}
.form {
    .grid {
        @apply gap-x-3;
    }

    :deep(.grid) {
        .input-group-container {
            max-width: unset !important;
        }
    }

    .custom-label {
        @apply text-center p-1;

        small {
            @apply text-[0.8rem] font-normal uppercase text-white;
        }
    }
}

.input-group-container {
    max-width: unset !important;
}
</style>
